import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, doc, getDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import Topbar from '../components/Topbar';
import Icon from '../components/Icon';
import Loading from '../components/Loading';
import './PromptDetail.css';

export default function PromptDetail({ accountId, role, onBack, onLogout }) {
  const { t } = useTranslation();
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');

  const isManager = role === 'owner' || role === 'admin';

  const fetchPrompts = async () => {
    const snap = await getDocs(collection(db, 'accounts', accountId, 'prompts'));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setPrompts(list);
    return list;
  };

  useEffect(() => {
    if (!accountId) return;
    fetchPrompts().then(list => {
      if (list.length > 0) setSelectedPromptId(prev => prev || list[0].id);
      setLoading(false);
    });
  }, [accountId]);

  useEffect(() => {
    if (!selectedPromptId || !accountId) return;
    async function fetchSystemPrompt() {
      const ref = doc(db, 'accounts', accountId, 'prompts', selectedPromptId);
      const snap = await getDoc(ref);
      const content = snap.data()?.systemPrompt || '';
      setSystemPrompt(content);
      setEditedPrompt(content);
      setIsEditing(false);
    }
    fetchSystemPrompt();
  }, [selectedPromptId, accountId]);

  const openPrompt = (id) => { setSelectedPromptId(id); setDetailOpen(true); };

  const handleSave = async () => {
    const ref = doc(db, 'accounts', accountId, 'prompts', selectedPromptId);
    await updateDoc(ref, { systemPrompt: editedPrompt });
    setSystemPrompt(editedPrompt);
    setIsEditing(false);
  };

  const createPrompt = async () => {
    if (!newName.trim()) return;
    const ref = await addDoc(collection(db, 'accounts', accountId, 'prompts'), { name: newName.trim(), systemPrompt: '' });
    setNewName('');
    await fetchPrompts();
    openPrompt(ref.id);
  };

  const renamePrompt = async () => {
    const current = prompts.find(p => p.id === selectedPromptId);
    const novo = window.prompt(t('prompts.renamePrompt'), current?.name || selectedPromptId);
    if (novo == null || !novo.trim()) return;
    await updateDoc(doc(db, 'accounts', accountId, 'prompts', selectedPromptId), { name: novo.trim() });
    await fetchPrompts();
  };

  const deletePrompt = async () => {
    if (selectedPromptId === 'atendimento') {
      setDeleteMsg(t('prompts.defaultCannotDelete'));
      return;
    }
    const inUse = await getDocs(query(collection(db, 'accounts', accountId, 'channels'), where('promptId', '==', selectedPromptId)));
    if (!inUse.empty) {
      setDeleteMsg(t('prompts.inUse'));
      return;
    }
    setDeleteMsg('');
    const current = prompts.find(p => p.id === selectedPromptId);
    if (!window.confirm(t('prompts.deleteConfirm', { name: current?.name || selectedPromptId }))) return;
    await deleteDoc(doc(db, 'accounts', accountId, 'prompts', selectedPromptId));
    const list = await fetchPrompts();
    setSelectedPromptId(list[0]?.id || null);
    setDetailOpen(false);
  };

  const selectedName = prompts.find(p => p.id === selectedPromptId)?.name || selectedPromptId;

  const formatHtml = (text) => text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/###[ ]?(.*)/g, '<h4>$1</h4>')
    .replace(/\n/g, '<br>');

  return (
    <div className="page">
      <Topbar onBack={onBack} onLogout={onLogout} crumbs={[t('prompts.crumb')]} />

      {loading ? (
        <Loading label={t('prompts.loading')} />
      ) : (
        <div className={`prompt-body ${detailOpen ? 'detail-open' : ''}`}>
          <aside className="prompt-list">
            <div className="list-label">{t('prompts.listLabel')}</div>
            {prompts.map(p => (
              <button
                key={p.id}
                className={`prompt-item ${selectedPromptId === p.id ? 'active' : ''}`}
                onClick={() => openPrompt(p.id)}
              >
                <Icon name="package" size={17} />{p.name || p.id}
              </button>
            ))}
            {isManager && (
              <div className="prompt-new">
                <input
                  className="field"
                  placeholder={t('prompts.newPlaceholder')}
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createPrompt()}
                />
                <button className="btn btn-ghost btn-sm" onClick={createPrompt} aria-label={t('prompts.createAria')}>
                  <Icon name="plus" size={16} />
                </button>
              </div>
            )}
          </aside>

          <div className="prompt-content">
            <div className="card system-prompt">
              <div className="prompt-title">
                <button className="prompt-back" onClick={() => setDetailOpen(false)} aria-label="Voltar">
                  <Icon name="arrow-left" size={20} />
                </button>
                <h3>{selectedName || t('prompts.systemPromptFallback')}</h3>
                {deleteMsg && <p className="form-error prompt-delete-msg">{deleteMsg}</p>}
                {!isEditing ? (
                  <div className="prompt-actions">
                    {isManager && selectedPromptId && (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={renamePrompt}>
                          <Icon name="edit" size={16} />{t('prompts.rename')}
                        </button>
                        {selectedPromptId !== 'atendimento' && (
                          <button className="btn btn-danger btn-sm" onClick={deletePrompt}>
                            <Icon name="trash" size={16} />{t('common.delete')}
                          </button>
                        )}
                        <button className="btn btn-primary btn-sm" onClick={() => setIsEditing(true)}>
                          <Icon name="edit" size={16} />{t('prompts.editContent')}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="prompt-actions">
                    <button className="btn btn-primary btn-sm" onClick={handleSave}>
                      <Icon name="check" size={16} />{t('common.save')}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setIsEditing(false); setEditedPrompt(systemPrompt); }}>
                      <Icon name="x" size={16} />{t('common.cancel')}
                    </button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <textarea
                  className="prompt-textarea"
                  value={editedPrompt}
                  onChange={(e) => setEditedPrompt(e.target.value)}
                />
              ) : (
                <div
                  className="prompt-paragraph"
                  dangerouslySetInnerHTML={{ __html: formatHtml(systemPrompt) }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
